import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	AdAccountListItem,
	InstagramAccountDetails,
	InstagramIntegration,
	MetaLimits
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { MetaInstagramAccountsService } from '@brand-lolly/app/services/meta-integrations/instagram-accounts.service';
import { extractMetaErrorMessage } from '@brand-lolly/app/shared/utils/meta-api-error.utils';
import { ConnectInstagramDialogComponent } from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/components/connect-channel/connect-instagram-dialog.component';
import {
	LO_DIALOG_DATA,
	LoAvatar,
	LoButton,
	LoChip,
	LoDialog,
	LoDialogRef,
	LoSkeleton,
	LoToastService
} from 'lo-design-system';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MetaCredentialsListComponent } from './meta-credentials-list.component';
import { MetaIgCapabilitiesGridComponent } from './meta-ig-capabilities-grid.component';
import { MetaPartnershipPairListComponent } from './meta-partnership-pair-list.component';

export interface MetaIgAccountModalData {
	account: InstagramIntegration;
	limits: MetaLimits | null;
	adAccounts: AdAccountListItem[];
}

export interface MetaIgAccountModalResult {
	refreshed?: boolean;
}

@Component({
	standalone: true,
	selector: 'meta-ig-account-modal',
	imports: [
		LoAvatar,
		LoButton,
		LoChip,
		LoDialog,
		LoSkeleton,
		MetaPartnershipPairListComponent,
		MetaIgCapabilitiesGridComponent,
		MetaCredentialsListComponent
	],
	template: `
		<lo-dialog [heading]="'@' + data.account.ig_username">
			<div class="content">
				<div class="lead">
					<lo-avatar
						[name]="data.account.ig_username"
						[src]="data.account.profile_picture_url || ''"
						platform="instagram"
					></lo-avatar>
					<span class="lo-type-text-sm">{{ subtitle }}</span>
				</div>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">Capabilities</h3>
					<meta-ig-capabilities-grid
						[supports]="data.account.supports"
						[connectionSources]="connectionSources"
					></meta-ig-capabilities-grid>
				</section>

				<section class="section">
					<div class="section-heading">
						<h3 class="lo-type-text-md-semibold">
							Partnership ADS
						</h3>
						@if (pairCount.total > 0) {
						<lo-chip>
							{{ pairCount.enabled }} of {{ pairCount.total }}
						</lo-chip>
						}
					</div>
					<p class="hint lo-type-text-sm">
						Partnership Ads per account. Inactive ad accounts can't
						be linked.
					</p>
					@if (data.account.id !== null) {
					<meta-partnership-pair-list
						[accountId]="data.account.id"
						[adAccounts]="data.adAccounts"
						(countsLoaded)="pairCount = $event"
					></meta-partnership-pair-list>
					}
				</section>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">Credentials</h3>
					@if (loadingDetails) {
					<div class="skeletons" aria-busy="true">
						@for (n of skeletonIds; track n) {
						<lo-skeleton variant="row"></lo-skeleton>
						}
					</div>
					} @else {
					<meta-credentials-list
						[credentials]="details?.credentials ?? []"
						[connectionSources]="connectionSources"
						(addPassword)="onAddPassword()"
					></meta-credentials-list>
					}
				</section>
			</div>
			<div loDialogActions>
				@if (canDelete) {
				<button
					lo-button
					variant="secondary"
					type="button"
					[loading]="deleteLoading"
					(click)="onDelete()"
				>
					Delete account
				</button>
				}
				<button lo-button type="button" (click)="close()">Done</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		`
			.content {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-xl);

				.lead {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);
				}

				.section {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-md);

					h3,
					p {
						margin: 0;
					}

					.section-heading {
						display: flex;
						align-items: center;
						gap: var(--lo-space-gap-sm);
					}

					.hint {
						color: var(--lo-color-text-secondary);
					}

					.skeletons {
						display: flex;
						flex-direction: column;
						gap: var(--lo-space-gap-sm);
					}
				}
			}
		`
	]
})
export class MetaIgAccountModalComponent implements OnInit, OnDestroy {
	details: InstagramAccountDetails | null = null;
	loadingDetails = true;
	deleteLoading = false;
	skeletonIds = [0, 1, 2];
	pairCount = { enabled: 0, total: 0 };

	protected readonly data = inject<MetaIgAccountModalData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<
			LoDialogRef<MetaIgAccountModalComponent, MetaIgAccountModalResult>
		>(LoDialogRef);
	private readonly toasts = inject(LoToastService);
	private destroy$ = new Subject<void>();

	constructor(
		private innerDialog: MatDialog,
		private service: MetaInstagramAccountsService
	) {}

	ngOnInit(): void {
		if (this.data.account.id === null) {
			this.loadingDetails = false;
			return;
		}
		this.service
			.get(this.data.account.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: details => {
					this.details = details;
					this.loadingDetails = false;
				},
				error: error => {
					this.loadingDetails = false;
					this.toasts.error(
						extractMetaErrorMessage(
							error,
							'Failed to load account details'
						)
					);
				}
			});
	}

	get connectionSources(): import('@brand-lolly/app/_models/brand-instagram-integration.model').ConnectionSource[] {
		return (
			this.details?.connection_sources ??
			this.data.account.connection_sources ??
			[]
		);
	}

	get canDelete(): boolean {
		return this.connectionSources.includes('instagram_oauth');
	}

	get subtitle(): string {
		const type = this.data.account.account_type ?? '';
		const source =
			this.data.account.fb_connection_id !== null
				? 'via Facebook'
				: 'Instagram OAuth';
		const max = this.data.limits?.instagram?.plan_max;
		const slot = this.data.account.slot_index;
		if (slot !== null && max) {
			return `${capitalize(type)} · ${source} · slot ${slot} of ${max}`;
		}
		return `${capitalize(type)} · ${source}`;
	}

	onAddPassword(): void {
		if (this.data.account.id === null) return;
		this.innerDialog
			.open(ConnectInstagramDialogComponent, {
				data: {
					allowedProviders: ['password'],
					mode: 'attach',
					integrationId: this.data.account.id,
					existingUsername: this.data.account.ig_username
				}
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				if (res?.refreshed) {
					this.dialogRef.close({ refreshed: true });
				}
			});
	}

	onDelete(): void {
		if (this.data.account.id === null) return;
		this.deleteLoading = true;
		this.service
			.remove(this.data.account.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.toasts.success('Account deleted');
					this.dialogRef.close({ refreshed: true });
				},
				error: error => {
					this.deleteLoading = false;
					this.toasts.error(
						extractMetaErrorMessage(error, 'Failed to delete')
					);
				}
			});
	}

	close(): void {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}

function capitalize(s: string): string {
	if (!s) return '';
	return s.charAt(0) + s.slice(1).toLowerCase();
}
